import { ExclamationTriangleIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import FindingUserImpact from "@/components/audits/FindingUserImpact";
import type { Finding } from "@/types";

type PlayerFindingCardProps = {
  finding: Finding;
};

export default function PlayerFindingCard({ finding }: PlayerFindingCardProps) {
  return (
    <article className="card rounded-l-none">
      <div className="group relative bg-white px-5 py-4">
        <div
          className={`absolute bottom-0 left-0 top-0 w-[2px] opacity-60 group-hover:opacity-100 ${priorityIndicatorClasses(finding.priority)}`}
        />
        <div className="flex items-start justify-between pl-1 md:pl-3">
          <div className="flex flex-1 flex-col items-start space-y-3">
            <div className="flex w-full flex-col items-start justify-between gap-y-2 lg:flex-row lg:items-center lg:gap-x-2 lg:gap-y-0">
              {finding.heuristic?.name ? (
                <span className="block text-xs font-semibold uppercase text-gray-500">{finding.heuristic.name}</span>
              ) : null}
              <span
                className={`flex items-center whitespace-nowrap rounded-full py-1 text-xs font-medium ${priorityTextClasses(finding.priority)}`}
              >
                <ExclamationTriangleIcon className="mr-1.5 h-3 w-3" aria-hidden="true" />
                {prioritySeverityLabel(finding.priority)}
              </span>
            </div>
            <h4 className="mb-2 text-base font-semibold text-gray-800">{finding.title || "Untitled issue"}</h4>
            {finding.description ? (
              <p className="mb-3 text-sm leading-relaxed text-gray-700">{finding.description}</p>
            ) : null}
            {finding.user_impact ? <FindingUserImpact userImpact={finding.user_impact} /> : null}
            {finding.suggestion ? (
              <div className="rounded-r-md border-l-4 border-gray-300 bg-gray-50 p-3">
                <div className="flex items-start">
                  <LightBulbIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-600">Suggestion</p>
                    <p className="text-sm leading-relaxed text-gray-700">{finding.suggestion}</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function prioritySeverityLabel(priority: Finding["priority"]) {
  if (priority === "critical" || priority === "high") return "High Severity";
  if (priority === "medium") return "Medium Severity";

  return "Low Severity";
}

function priorityTextClasses(priority: Finding["priority"]) {
  if (priority === "critical" || priority === "high") return "text-error-dark";
  if (priority === "medium") return "text-warning-dark";

  return "text-primary-700";
}

function priorityIndicatorClasses(priority: Finding["priority"]) {
  if (priority === "critical" || priority === "high") return "bg-error";
  if (priority === "medium") return "bg-warning";

  return "bg-primary";
}
